const MONTHS = {
  enero: 0,
  febrero: 1,
  marzo: 2,
  abril: 3,
  mayo: 4,
  junio: 5,
  julio: 6,
  agosto: 7,
  septiembre: 8,
  setiembre: 8,
  octubre: 9,
  noviembre: 10,
  diciembre: 11,
};

const WEEKDAYS = {
  domingo: 0,
  lunes: 1,
  martes: 2,
  miercoles: 3,
  jueves: 4,
  viernes: 5,
  sabado: 6,
};

const SMALL_NUMBERS = {
  cero: 0,
  un: 1,
  uno: 1,
  una: 1,
  dos: 2,
  tres: 3,
  cuatro: 4,
  cinco: 5,
  seis: 6,
  siete: 7,
  ocho: 8,
  nueve: 9,
  diez: 10,
  once: 11,
  doce: 12,
  trece: 13,
  catorce: 14,
  quince: 15,
  dieciseis: 16,
  diecisiete: 17,
  dieciocho: 18,
  diecinueve: 19,
  veinte: 20,
  veintiuno: 21,
  veintidos: 22,
  veintitres: 23,
  veinticuatro: 24,
  veinticinco: 25,
  veintiseis: 26,
  veintisiete: 27,
  veintiocho: 28,
  veintinueve: 29,
  treinta: 30,
  cuarenta: 40,
  cincuenta: 50,
  sesenta: 60,
  setenta: 70,
  ochenta: 80,
  noventa: 90,
  cien: 100,
  ciento: 100,
};

export function normalizeVoiceText(value = "") {
  return String(value)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[¿?¡!.,;:()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function isAffirmative(value) {
  const text = normalizeVoiceText(value);
  return ["si", "dale", "confirmar", "confirmo", "guardar", "guardalo", "guardala", "ok", "okay", "listo", "hacele", "hacerlo"].some((token) => text === token || text.startsWith(`${token} `));
}

export function isNegative(value) {
  const text = normalizeVoiceText(value);
  return ["no", "cancelar", "cancela", "cancelalo", "cancelala", "volver", "todavia no"].some((token) => text === token || text.startsWith(`${token} `));
}

function spokenInteger(text) {
  const normalized = normalizeVoiceText(text).replace(/\by\b/g, " ");
  const words = normalized.split(" ").filter(Boolean);
  let total = 0;
  let current = 0;
  let saw = false;

  for (const word of words) {
    if (Object.prototype.hasOwnProperty.call(SMALL_NUMBERS, word)) {
      current += SMALL_NUMBERS[word];
      saw = true;
      continue;
    }
    if (word === "mil") {
      current = (current || 1) * 1000;
      total += current;
      current = 0;
      saw = true;
      continue;
    }
    if (word === "millon" || word === "millones") {
      current = (current || 1) * 1000000;
      total += current;
      current = 0;
      saw = true;
      continue;
    }
  }

  return saw ? total + current : null;
}

export function parseVoiceNumber(value) {
  const raw = String(value || "").trim().toLowerCase();
  const text = normalizeVoiceText(value);
  if (!text) return null;

  const explicit = raw.match(/-?\d[\d.,]*/);
  let number = null;
  if (explicit) {
    let token = explicit[0];
    const comma = token.lastIndexOf(",");
    const dot = token.lastIndexOf(".");
    if (comma >= 0 && dot >= 0) {
      if (comma > dot) token = token.replace(/\./g, "").replace(",", ".");
      else token = token.replace(/,/g, "");
    } else if (comma >= 0) {
      const decimals = token.length - comma - 1;
      token = decimals === 3 ? token.replace(/,/g, "") : token.replace(",", ".");
    } else if (dot >= 0) {
      const decimals = token.length - dot - 1;
      if (decimals === 3 && /^-?\d{1,3}\.\d{3}$/.test(token)) token = token.replace(".", "");
    }
    number = Number(token);
  } else {
    number = spokenInteger(text);
  }

  if (!Number.isFinite(number)) return null;
  if (/\b(luca|lucas|k)\b/.test(text)) number *= 1000;
  if (/\b(millon|millones)\b/.test(text) && explicit) number *= 1000000;
  return number;
}

export function matchVoiceOption(value, options = []) {
  const text = normalizeVoiceText(value);
  if (!text) return null;
  let best = null;
  let bestScore = -1;

  for (const option of options) {
    const candidates = [option.label, option.value, ...(option.aliases || [])]
      .filter(Boolean)
      .map(normalizeVoiceText);
    for (const candidate of candidates) {
      if (!candidate) continue;
      let score = -1;
      if (text === candidate) score = 100;
      else if (text.includes(candidate)) score = 80 + Math.min(candidate.length, 15);
      else if (candidate.includes(text) && text.length >= 3) score = 60 + Math.min(text.length, 15);
      else {
        const candidateWords = new Set(candidate.split(" "));
        const common = text.split(" ").filter((word) => candidateWords.has(word)).length;
        if (common) score = common * 10;
      }
      if (score > bestScore) {
        bestScore = score;
        best = option;
      }
    }
  }
  return bestScore >= 10 ? best : null;
}

function localDateTimeValue(date) {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function parseHour(text, fallbackHour = 9) {
  const normalized = normalizeVoiceText(text);
  const afterAt = normalized.match(/(?:a\s+las?|a\s+la)\s+(\d{1,2})(?:[:\s](\d{2}))?/);
  const anyNumeric = normalized.match(/\b(\d{1,2})(?:[:\s](\d{2}))?\b/);
  const numeric = afterAt || anyNumeric;
  let hour = numeric ? Number(numeric[1]) : null;
  let minute = numeric?.[2] ? Number(numeric[2]) : 0;
  if (hour === null) {
    const tailMatch = normalized.match(/(?:a\s+las?|a\s+la)\s+(.+)$/);
    const words = spokenInteger(tailMatch?.[1] || normalized);
    if (Number.isFinite(words) && words <= 24) hour = words;
  }
  if (hour === null) hour = fallbackHour;
  if (/\b(tarde|noche|pm)\b/.test(normalized) && hour < 12) hour += 12;
  if (/\b(de la manana|por la manana|am)\b/.test(normalized) && hour === 12) hour = 0;
  if (!/\b(de la manana|por la manana|am|tarde|noche|pm)\b/.test(normalized) && hour >= 1 && hour <= 7) hour += 12;
  if (/\bmediodia\b/.test(normalized)) hour = 12;
  if (/\bmedianoche\b/.test(normalized)) hour = 0;
  if (/\by media\b/.test(normalized)) minute = 30;
  if (/\by cuarto\b/.test(normalized)) minute = 15;
  return { hour: Math.min(Math.max(hour, 0), 23), minute: Math.min(Math.max(minute, 0), 59) };
}

export function parseVoiceDateTime(value, baseDate = new Date()) {
  const text = normalizeVoiceText(value);
  if (!text) return null;
  const date = new Date(baseDate);
  date.setSeconds(0, 0);

  if (/\bpasado manana\b/.test(text)) date.setDate(date.getDate() + 2);
  else if (/\bmanana\b/.test(text)) date.setDate(date.getDate() + 1);
  else if (!/\bhoy\b/.test(text)) {
    let matchedWeekday = null;
    for (const [name, day] of Object.entries(WEEKDAYS)) {
      if (text.includes(name)) { matchedWeekday = day; break; }
    }
    if (matchedWeekday !== null) {
      let delta = (matchedWeekday - date.getDay() + 7) % 7;
      if (delta === 0 && !/\bhoy\b/.test(text)) delta = 7;
      date.setDate(date.getDate() + delta);
    } else {
      const explicitDate = text.match(/\b(\d{1,2})\s+(?:de\s+)?([a-z]+)(?:\s+(?:de\s+)?(\d{4}))?/);
      if (explicitDate && Object.prototype.hasOwnProperty.call(MONTHS, explicitDate[2])) {
        date.setDate(Number(explicitDate[1]));
        date.setMonth(MONTHS[explicitDate[2]]);
        if (explicitDate[3]) date.setFullYear(Number(explicitDate[3]));
      }
    }
  }

  const { hour, minute } = parseHour(text, date.getHours() || 9);
  date.setHours(hour, minute, 0, 0);
  return localDateTimeValue(date);
}

export function parseVoicePhone(value) {
  const raw = String(value || "").trim();
  const normalizedWords = normalizeVoiceText(raw)
    .replace(/cero/g, "0")
    .replace(/uno/g, "1")
    .replace(/dos/g, "2")
    .replace(/tres/g, "3")
    .replace(/cuatro/g, "4")
    .replace(/cinco/g, "5")
    .replace(/seis/g, "6")
    .replace(/siete/g, "7")
    .replace(/ocho/g, "8")
    .replace(/nueve/g, "9");
  const digits = normalizedWords.replace(/[^+\d]/g, "");
  return digits.length >= 6 ? digits : raw;
}

export function findFieldByVoice(value, fields = []) {
  const text = normalizeVoiceText(value);
  if (!text) return null;
  let best = null;
  let bestScore = -1;
  for (const field of fields) {
    const candidates = [field.label, field.name, ...(field.aliases || [])].filter(Boolean).map(normalizeVoiceText);
    for (const candidate of candidates) {
      if (!candidate) continue;
      let score = -1;
      if (text === candidate) score = 100;
      else if (text.includes(candidate)) score = 80 + Math.min(candidate.length, 15);
      else if (candidate.includes(text) && text.length >= 3) score = 55;
      if (score > bestScore) { bestScore = score; best = field; }
    }
  }
  return bestScore >= 55 ? best : null;
}

export function cleanFieldCommand(value, field) {
  const raw = String(value || "").trim();
  const normalized = normalizeVoiceText(raw);
  const candidates = [field?.label, field?.name, ...(field?.aliases || [])].filter(Boolean).map(normalizeVoiceText);
  for (const candidate of candidates) {
    const patterns = [
      `cambiar ${candidate} a `,
      `cambia ${candidate} a `,
      `poner ${candidate} `,
      `pone ${candidate} `,
      `${candidate} `,
    ];
    for (const prefix of patterns) {
      if (normalized.startsWith(prefix)) {
        const originalWords = raw.split(/\s+/);
        const normalizedWords = normalized.split(/\s+/);
        const prefixWords = prefix.trim().split(/\s+/).length;
        if (originalWords.length >= prefixWords) return originalWords.slice(prefixWords).join(" ").trim();
        if (normalizedWords.length >= prefixWords) return normalizedWords.slice(prefixWords).join(" ").trim();
      }
    }
  }
  return raw;
}

function localYmd(value = new Date()) {
  const pad = (number) => String(number).padStart(2, "0");
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

function dateFromParts(year, monthIndex, day) {
  const date = new Date(year, monthIndex, day, 12, 0, 0, 0);
  if (date.getFullYear() !== year || date.getMonth() !== monthIndex || date.getDate() !== day) return null;
  return date;
}

function startOfWeek(baseDate) {
  const date = new Date(baseDate);
  date.setHours(12, 0, 0, 0);
  const delta = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - delta);
  return date;
}

function endOfMonth(baseDate) {
  return new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0, 12, 0, 0, 0);
}

function parseDateFragment(value, baseDate = new Date(), inherited = {}) {
  const raw = String(value || "").trim();
  const text = normalizeVoiceText(raw);
  if (!text) return null;
  const base = new Date(baseDate);
  base.setHours(12, 0, 0, 0);

  if (/\banteayer\b/.test(text)) { base.setDate(base.getDate() - 2); return base; }
  if (/\bayer\b/.test(text)) { base.setDate(base.getDate() - 1); return base; }
  if (/\bhoy\b/.test(text)) return base;
  if (/\bpasado manana\b/.test(text)) { base.setDate(base.getDate() + 2); return base; }
  if (/\bmanana\b/.test(text)) { base.setDate(base.getDate() + 1); return base; }

  const iso = raw.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  if (iso) return dateFromParts(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));

  const slash = raw.match(/\b(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\b/);
  if (slash) {
    let year = slash[3] ? Number(slash[3]) : (inherited.year ?? base.getFullYear());
    if (year < 100) year += 2000;
    return dateFromParts(year, Number(slash[2]) - 1, Number(slash[1]));
  }

  const dayMonth = text.match(/\b(\d{1,2})\s+(?:de\s+)?([a-z]+)(?:\s+(?:de\s+)?(\d{4}))?\b/);
  if (dayMonth && Object.prototype.hasOwnProperty.call(MONTHS, dayMonth[2])) {
    return dateFromParts(Number(dayMonth[3] || inherited.year || base.getFullYear()), MONTHS[dayMonth[2]], Number(dayMonth[1]));
  }

  const dayOnly = text.match(/(?:\bdia\s+|^)(\d{1,2})(?:\b|$)/);
  if (dayOnly && (inherited.monthIndex !== undefined || /\bdia\s+/.test(text))) {
    return dateFromParts(Number(inherited.year || base.getFullYear()), inherited.monthIndex ?? base.getMonth(), Number(dayOnly[1]));
  }

  for (const [name, weekday] of Object.entries(WEEKDAYS)) {
    if (!text.includes(name)) continue;
    const date = new Date(base);
    if (/\b(semana pasada|pasado|anterior|ultimo)\b/.test(text)) {
      let delta = (date.getDay() - weekday + 7) % 7;
      if (delta === 0) delta = 7;
      date.setDate(date.getDate() - delta);
      return date;
    }
    if (/\b(proximo|que viene|siguiente)\b/.test(text)) {
      let delta = (weekday - date.getDay() + 7) % 7;
      if (delta === 0) delta = 7;
      date.setDate(date.getDate() + delta);
      return date;
    }
    const delta = (date.getDay() - weekday + 7) % 7;
    date.setDate(date.getDate() - delta);
    return date;
  }

  return null;
}

export function parseVoiceDateKey(value, baseDate = new Date()) {
  const parsed = parseDateFragment(value, baseDate);
  return parsed ? localYmd(parsed) : null;
}

export function parseVoiceDateRange(value, baseDate = new Date()) {
  const raw = String(value || "").trim();
  const text = normalizeVoiceText(raw);
  if (!text) return null;
  const base = new Date(baseDate);
  base.setHours(12, 0, 0, 0);

  const lastDays = text.match(/\bultimos?\s+(\d{1,3})\s+dias?\b/);
  if (lastDays) {
    const count = Math.max(1, Math.min(370, Number(lastDays[1])));
    const from = new Date(base);
    from.setDate(from.getDate() - (count - 1));
    return { from: localYmd(from), to: localYmd(base), label: `los últimos ${count} días` };
  }

  if (/\besta semana\b/.test(text)) {
    return { from: localYmd(startOfWeek(base)), to: localYmd(base), label: "esta semana" };
  }
  if (/\bsemana pasada\b/.test(text)) {
    const currentStart = startOfWeek(base);
    const from = new Date(currentStart); from.setDate(from.getDate() - 7);
    const to = new Date(currentStart); to.setDate(to.getDate() - 1);
    return { from: localYmd(from), to: localYmd(to), label: "la semana pasada" };
  }
  if (/\beste mes\b/.test(text)) {
    const from = new Date(base.getFullYear(), base.getMonth(), 1, 12);
    return { from: localYmd(from), to: localYmd(base), label: "este mes" };
  }
  if (/\bmes pasado\b/.test(text)) {
    const from = new Date(base.getFullYear(), base.getMonth() - 1, 1, 12);
    const to = endOfMonth(from);
    return { from: localYmd(from), to: localYmd(to), label: "el mes pasado" };
  }

  const sameMonth = text.match(/\b(?:del|desde el?)\s+(\d{1,2})\s+(?:al|hasta el?)\s+(\d{1,2})\s+(?:de\s+)?([a-z]+)(?:\s+(?:de\s+)?(\d{4}))?\b/);
  if (sameMonth && Object.prototype.hasOwnProperty.call(MONTHS, sameMonth[3])) {
    const year = Number(sameMonth[4] || base.getFullYear());
    const fromDate = dateFromParts(year, MONTHS[sameMonth[3]], Number(sameMonth[1]));
    const toDate = dateFromParts(year, MONTHS[sameMonth[3]], Number(sameMonth[2]));
    if (fromDate && toDate) return { from: localYmd(fromDate), to: localYmd(toDate), label: `del ${sameMonth[1]} al ${sameMonth[2]} de ${sameMonth[3]}` };
  }

  const separators = [" hasta ", " al ", " a ", " y "];
  const cleaned = text.replace(/^.*?\b(?:desde|del|entre)\s+/, "");
  for (const separator of separators) {
    const index = cleaned.indexOf(separator);
    if (index <= 0) continue;
    const left = cleaned.slice(0, index).trim();
    const right = cleaned.slice(index + separator.length).trim();
    const rightDate = parseDateFragment(right, base);
    if (!rightDate) continue;
    const leftDate = parseDateFragment(left, base, { year: rightDate.getFullYear(), monthIndex: rightDate.getMonth() });
    if (!leftDate) continue;
    const fromDate = leftDate <= rightDate ? leftDate : rightDate;
    const toDate = leftDate <= rightDate ? rightDate : leftDate;
    return { from: localYmd(fromDate), to: localYmd(toDate), label: `del ${localYmd(fromDate)} al ${localYmd(toDate)}` };
  }

  const single = parseDateFragment(text, base);
  if (single && /\b(hoy|ayer|anteayer|lunes|martes|miercoles|jueves|viernes|sabado|domingo|dia\s+\d|\d{1,2}\s+de\s+[a-z]+|\d{1,2}\/\d{1,2})\b/.test(text)) {
    const key = localYmd(single);
    return { from: key, to: key, label: key };
  }

  for (const [monthName, monthIndex] of Object.entries(MONTHS)) {
    const monthPattern = new RegExp(`\\b(?:en|de|durante)\\s+${monthName}(?:\\s+(?:de\\s+)?(\\d{4}))?\\b`);
    const match = text.match(monthPattern);
    if (match) {
      const year = Number(match[1] || base.getFullYear());
      const from = dateFromParts(year, monthIndex, 1);
      if (!from) return null;
      const to = endOfMonth(from);
      return { from: localYmd(from), to: localYmd(to), label: `${monthName} de ${year}` };
    }
  }

  return null;
}
